











































import AppFooter from "@/components/FooterNavigation.vue";
import "./style/app.scss";
import { app } from "@/store";
import { enrollUi } from '@/store/enrollUi';
import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import useServiceUpdate from "@/utilities/useServiceUpdate";

export default defineComponent({
    components: {
        AppFooter,
        EnrollDialog: () => import('@/views/components/Dialogs/EnrollDialog.vue'),
        AppNotificationDialog: () => import('@/views/components/Dialogs/AppNotificationDialog.vue')
    },
    setup() {
        useServiceUpdate(); // sets up auto-refresh behavior
        const isProcessing = computed(() => app.isProcessing);
        const processingLabel = computed(() => app.processingLabel);
        const countdown = computed(() => app.countdown);

        const title = computed<string>(() => app.title ? app.title : "");
        const topbar = computed<boolean>(() => app.showTopBar);
        const footer = computed<boolean>(() => app.showFooter);

        const error = computed<string>(() => app.error ?? "");
        const alarm = computed<string>(() => app.alarm ?? "");
        const success = computed<string>(() => app.success ?? "");
        const alert = computed<string>(() => app.alert ?? "");

        // --- unified dialog state ---
        const statusDialogOpen = ref(false);
        const currentTone = ref<'warning' | 'alarm' | 'alert' | 'success'>('warning');
        const currentTitle = ref<string>('');
        const currentMessage = ref<string>('');
            let errorDismissTimer: ReturnType<typeof setTimeout> | null = null;

        // Priority: alarm > alert > warning(error) > success
        function refreshDialogFromStore() {
            if (alarm.value) {
                currentTone.value = 'alarm';
                currentTitle.value = 'Alarm triggered';
                currentMessage.value = alarm.value;
                statusDialogOpen.value = true;
                return;
            }
            if (alert.value) {
                currentTone.value = 'alert';
                currentTitle.value = 'Alert';
                currentMessage.value = alert.value;
                statusDialogOpen.value = true;
                return;
            }
            if (error.value) {
                currentTone.value = 'warning';
                currentTitle.value = 'Warning';
                currentMessage.value = error.value;
                statusDialogOpen.value = true;
                return;
            }
            if (success.value) {
                currentTone.value = 'success';
                currentTitle.value = 'Success';
                currentMessage.value = success.value;
                statusDialogOpen.value = true;
                return;
            }
            // none active
            statusDialogOpen.value = false;
            currentMessage.value = '';
        }

        // Dismiss clears only the active source in priority order
        function dismissStatusDialog() {
            switch (currentTone.value) {
                case 'alarm': app.alarm = ""; break;
                case 'alert': app.alert = ""; break;
                case 'warning': app.error = ""; break;
                case 'success': app.success = ""; break;
            }
            // refresh will close or move to next pending tone
            refreshDialogFromStore();
        }

        // Watch store fields and update the unified dialog
        watch(() => app.alarm, () => refreshDialogFromStore(), { immediate: true });
        watch(() => app.alert, () => refreshDialogFromStore(), { immediate: true });
            watch(() => app.error, (nv) => {
                refreshDialogFromStore();
                if (!nv) {
                    if (errorDismissTimer) {
                        clearTimeout(errorDismissTimer);
                        errorDismissTimer = null;
                    }
                    app.errorAutoDismiss = true;
                    app.errorAutoDismissExplicit = false;
                    return;
                }

                if (!app.errorAutoDismissExplicit) {
                    app.errorAutoDismiss = true;
                }

                if (app.errorAutoDismiss) {
                    if (errorDismissTimer) {
                        clearTimeout(errorDismissTimer);
                    }
                    const timeoutMs = app.errorAutoDismissMs ?? 10000;
                    errorDismissTimer = setTimeout(() => { app.error = ""; }, timeoutMs);
                }
            }, { immediate: true });
        watch(() => app.success, (nv) => {
            refreshDialogFromStore();
            if (nv) {
                setTimeout(() => { app.success = ""; }, 10000); // auto-dismiss
            }
        }, { immediate: true });

        // Also close if the user clicks out or ESC via v-model
        watch(statusDialogOpen, (open) => {
            if (!open) {
                // user closed the dialog -> clear whichever tone is active
                dismissStatusDialog();
            }
        });

        return {
            title,
            topbar,
            footer,
            isProcessing,
            processingLabel,
            countdown,
            error,
            alarm,
            success,
            alert,
            statusDialogOpen,
            currentTone,
            currentTitle,
            currentMessage,
            dismissStatusDialog,
            enrollUi
        };
    }
});
